/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled, { css } from 'styled-components';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  useUnmount,
  useState,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import { generatePatternStyles, hasGradient } from '@web-stories-wp/patterns';
import { useUnits } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { useStory, useFont, useTransform } from '../../app';
import RichTextEditor from '../../components/richText/editor';
import { FONT_SIZE_DATA_CSS_PROPERTY } from '../../components/richText/formatters/fontSize';
import {
  elementFillContent,
  elementWithFont,
  elementWithTextParagraphStyle,
  elementWithBackgroundColor,
  elementWithBorder,
  elementWithHighlightBorderRadius,
  elementWithTextBoxShadow,
  elementWithTextShadow,
} from '../shared';
import StoryPropTypes from '../../types';
import { BACKGROUND_TEXT_MODE, TEXT_SHADOW_TARGET } from '../../constants';
import {
  getTextBoxAttachmentReflow,
  getTextElementHeightResizeUpdates,
} from '../../utils/getTextHeightResizeUpdates';
import { useTransformHandler } from '../../components/transform';
import {
  getBorderPositionCSS,
  shouldDisplayBorder,
} from '../../utils/elementBorder';
import useCSSVarColorTransformHandler from '../shared/useCSSVarColorTransformHandler';
import useColorTransformHandler from '../shared/useColorTransformHandler';
import { generateParagraphTextStyle, getHighlightLineheight } from './util';
import {
  calcFontMetrics,
  getStandardHighlightHeight,
  getSvgTextVerticalPosition,
  shouldSyncSvgTextWithHtml,
  shouldUseSvgTextFill,
} from './utils';
import { SvgTextDisplay } from './svgTextDisplay';
import { useRangeGradientPreview } from './useGradientPreview';
import {
  DEFAULT_GRADIENT_FALLBACK_COLOR,
  getGradientFallbackColor,
} from '../../utils/textGradient';
import RichTextEditorWrapper from './richTextEditorWrapper';
import { getMeasuredLineBreakContent } from './utils/getMeasuredLineBreakContent';
import {
  getFirstSvgTextRect,
  getFirstTextRect,
} from './utils/getTextRenderRects';
import {
  collectTextFontUsage,
  getBoundedTypographyScale,
  getLiveTypographyFontSizeCss,
  hasEffectiveInlineFontSizeRuns,
  hasEffectiveInlineLineHeightRuns,
  hasMixedTypographyMarkers,
  materializeTextContent,
  parseInlineFontSize,
  scaleInlineFontSizes,
  setLiveTypographyScale,
} from './utils/mixedTypography';
import { elementWithTextList } from './listStyles';
import { hasSemanticList } from './utils/listStyle';
import {
  getTextBoxAttachmentTargetTransformId,
  getTextBoxAttachmentStack,
  isTextBoxAttachment,
} from '../../utils/textBoxAttachment';
import NativeTextLayout from './nativeTextLayout';
import { sanitizeRichText } from '../../utils/sanitizeMarkup';
import useSanitizedTextElement from './utils/useSanitizedTextElement';
import useTextLayoutElement from './utils/useTextLayoutElement';
import {
  getNormalizedTextShadow,
  shouldApplyFillTextBoxShadow,
} from './getTextShadowStyles';

// Wrapper bounds the text editor within the element bounds. The resize
// logic updates the height of this element to show the new height based
// on the content and properties.
const Wrapper = styled.div`
  ${elementFillContent}
  ${elementWithBackgroundColor}
  ${({ $applyTextBoxShadow }) =>
    $applyTextBoxShadow && elementWithTextBoxShadow}

  --faux-selection-color: inherit;

  span {
    box-decoration-break: clone;
  }
`;

// TextBox defines all text display properties and is used for measuring
// of text height. This element has an unbounded height (bottom) so that
// it can be used for height measurement.
const TextBox = styled.div`
  ${elementWithFont}
  ${elementWithTextParagraphStyle}
  ${elementWithTextList}

  opacity: ${({ opacity }) =>
    typeof opacity !== 'undefined' ? opacity / 100 : null};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const TextBoxPadded = styled(TextBox)(() => ({
  right: 'auto',
  width: '100%',
}));

const EditTextBox = styled(TextBox)`
  ${({ hasSvgFill }) =>
    !hasSvgFill &&
    css`
      ${elementWithTextShadow}
    `}

  ${({ hasHighlightBackgroundTextMode }) =>
    hasHighlightBackgroundTextMode &&
    `
      padding-top: 0;
      padding-bottom: 0;
    `}
`;

const Highlight = styled.span`
  ${elementWithHighlightBorderRadius}
  ${({ $usesMixedTypographyLayout }) =>
    !$usesMixedTypographyLayout && elementWithTextBoxShadow}
  ${({ highlightColor, element }) =>
    generatePatternStyles(highlightColor, 'background', null, element)};
  color: transparent !important;
  * {
    color: transparent !important;
  }
  padding: ${({ $usesMixedTypographyLayout, padding }) =>
    $usesMixedTypographyLayout ? 0 : padding};

  ${({
    $hasMixedLineHeight,
    $usesMixedTypographyLayout,
    highlightColor,
    element,
    padding,
  }) =>
    $usesMixedTypographyLayout &&
    css`
      -webkit-background-clip: text;
      background-clip: text;
      font-size: 0;
      line-height: 0;

      > [data-st-text-line],
      > [data-st-text-line] > [data-st-text-line-band] {
        background: inherit;
      }

      > [data-st-text-line] {
        -webkit-background-clip: text;
        align-items: center;
        background-clip: text;
        display: inline-flex;
        vertical-align: top;
      }

      > [data-st-text-line] > [data-st-text-line-band] {
        -webkit-background-clip: border-box;
        background-clip: border-box;
        border-radius: inherit;
        box-sizing: border-box;
        box-decoration-break: clone;
        display: inline-block;
        line-height: 1em;
        padding: ${padding};
        position: relative;
        ${elementWithTextBoxShadow}
      }

      ${$hasMixedLineHeight &&
      css`
        background: none;

        > [data-st-text-line] {
          background: none;
        }

        > [data-st-text-line] > [data-st-text-line-band] {
          ${generatePatternStyles(highlightColor, 'background', null, element)}
        }
      `}
    `}
`;

const OutsideBorder = styled.div`
  ${elementWithBorder}
  ${({ border }) => border && getBorderPositionCSS(border)}
  overflow: ${({ $allowTextOverflow }) =>
    $allowTextOverflow ? 'visible' : 'hidden'};
`;

function updateInlineFontSizePreview(root, scale, dataToEditorY) {
  root?.querySelectorAll('span').forEach((span) => {
    const storedSize =
      span.getAttribute('data-st-font-size') ||
      span.style.getPropertyValue(FONT_SIZE_DATA_CSS_PROPERTY);
    const fontSize = parseInlineFontSize(storedSize);
    if (fontSize !== null) {
      span.style.fontSize = `${dataToEditorY(fontSize * scale)}px`;
    }
  });
}

function TextEditCanvas({
  element: untrustedElement,
  box: { x, y, height, rotationAngle },
  editWrapper,
  onResize,
  hasTextBoxBackdrop = false,
  outerDataToEditorX,
  outerDataToEditorY,
  outerToNativeX,
  outerToNativeY,
}) {
  const element = useSanitizedTextElement(untrustedElement);
  const {
    id,
    content,
    fontReferences,
    backgroundColor,
    backgroundTextMode,
    border,
    borderRadius,
    lastUpdatedBy,
    opacity,
    height: elementHeight,
    ...rest
  } = element;
  const { font, fontSize, width: elementWidth } = rest;

  const { dataToEditorX, dataToEditorY, editorToDataX, editorToDataY } =
    useUnits(
      ({
        actions: { dataToEditorX, dataToEditorY, editorToDataX, editorToDataY },
      }) => ({
        dataToEditorX,
        dataToEditorY,
        editorToDataX,
        editorToDataY,
      })
    );

  const [svgBorderProps, setSvgBorderProps] = useState({
    content,
    fontReferences,
  });
  const editorContent = svgBorderProps.content;
  const hasList = hasSemanticList(editorContent ?? content);
  const supportedBackgroundTextMode =
    hasList && backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
      ? BACKGROUND_TEXT_MODE.FILL
      : backgroundTextMode;
  const [liveSvgSize, setLiveSvgSize] = useState(null);
  const [requestedTypographyScale, setRequestedTypographyScale] = useState(1);
  const liveTypographyScale = useMemo(
    () =>
      getBoundedTypographyScale(
        { content: svgBorderProps.content, fontSize: element.fontSize },
        requestedTypographyScale
      ),
    [element.fontSize, requestedTypographyScale, svgBorderProps.content]
  );
  const [svgEditOffset, setSvgEditOffset] = useState({ x: 0, y: 0 });
  const previewContent = useMemo(
    () => scaleInlineFontSizes(svgBorderProps.content, liveTypographyScale),
    [liveTypographyScale, svgBorderProps.content]
  );
  const liveTextElement = useMemo(
    () => ({
      ...element,
      content: svgBorderProps.content,
      fontReferences: svgBorderProps.fontReferences,
    }),
    [element, svgBorderProps.content, svgBorderProps.fontReferences]
  );
  const layoutElement = useTextLayoutElement(liveTextElement);
  const hasMixedLineHeight = useMemo(
    () => !hasList && hasEffectiveInlineLineHeightRuns(layoutElement),
    [hasList, layoutElement]
  );
  const materializeEditorFontSizes = useMemo(
    () =>
      !hasList &&
      (hasEffectiveInlineFontSizeRuns(layoutElement) || hasMixedLineHeight),
    [hasList, hasMixedLineHeight, layoutElement]
  );
  const fontFaceSetConfigs = useMemo(
    () => collectTextFontUsage(layoutElement),
    [layoutElement]
  );

  const { loadedFonts = [], maybeEnqueueFontStyle } = useFont(
    ({ actions: { maybeEnqueueFontStyle }, state: { loadedFonts } }) => ({
      loadedFonts,
      maybeEnqueueFontStyle,
    })
  );

  const usedRenderFamilies = useMemo(
    () => new Set(fontFaceSetConfigs.map(({ renderFamily }) => renderFamily)),
    [fontFaceSetConfigs]
  );
  const fontLoaded = useMemo(
    () =>
      loadedFonts.filter((loadedFont) =>
        usedRenderFamilies.has(
          loadedFont.renderFamily || loadedFont.font.family
        )
      ),
    [loadedFonts, usedRenderFamilies]
  );

  const {
    height: verticalTrimHeight,
    heightWithoutVerticalTrim,
    marginOffsetTop,
    marginOffsetBottom,
    standardTrimHeight,
  } = useMemo(
    () =>
      calcFontMetrics({
        element: {
          content: previewContent,
          fontReferences: svgBorderProps.fontReferences,
          color: element.color,
          font: element.font,
          fontSize: element.fontSize * liveTypographyScale,
          fontStyle: element.fontStyle,
          fontWeight: element.fontWeight,
          lineHeight: element.lineHeight,
          verticalTrim: element.verticalTrim,
          width: element.width,
          backgroundTextMode: supportedBackgroundTextMode,
          padding: element.padding,
          listStyle: element.listStyle,
        },
        dataToEditorX,
        dataToEditorY,
        calculateStandardTrimHeight: true,
      }),
    [
      element.color,
      element.font,
      element.fontSize,
      element.fontStyle,
      element.fontWeight,
      element.lineHeight,
      element.verticalTrim,
      element.width,
      supportedBackgroundTextMode,
      element.padding,
      element.listStyle,
      dataToEditorX,
      dataToEditorY,
      fontLoaded,
      liveTypographyScale,
      previewContent,
      svgBorderProps.fontReferences,
    ]
  );

  const textProps = {
    ...generateParagraphTextStyle(
      rest,
      dataToEditorX,
      dataToEditorY,
      undefined,
      marginOffsetTop,
      element
    ),
    font,
    element,
    dataToEditorX,
    dataToEditorY,
    $hasList: hasList,
    backgroundColor,
    opacity: hasTextBoxBackdrop ? undefined : opacity,
    marginOffsetTop,
    verticalPadding: dataToEditorX(rest.padding?.vertical || 0),
    textBoxVerticalPadding: dataToEditorY(rest.padding?.vertical || 0),
    ...(supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT && {
      lineHeight: getHighlightLineheight(
        rest.lineHeight,
        dataToEditorX(rest.padding?.vertical || 0)
      ),
      backgroundColor: null,
      highlightColor: backgroundColor,
    }),
    ...(supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.NONE && {
      backgroundColor: null,
    }),
  };

  const { padding: _, ...highlightTextProps } = textProps;
  const hasHighlightBackgroundTextMode =
    supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT;
  const hasMixedTypography = useMemo(
    () => hasMixedTypographyMarkers(svgBorderProps.content),
    [svgBorderProps.content]
  );
  const usesMixedTypographyHighlightLayout =
    hasHighlightBackgroundTextMode && hasMixedTypography;
  const editorLineHeightToCss = useCallback(
    (lineHeight) =>
      hasHighlightBackgroundTextMode && hasMixedLineHeight
        ? getHighlightLineheight(lineHeight, textProps.verticalPadding)
        : lineHeight,
    [
      hasHighlightBackgroundTextMode,
      hasMixedLineHeight,
      textProps.verticalPadding,
    ]
  );
  const hasGradientFill = useMemo(
    () => hasGradient(element.color),
    [element.color]
  );
  const rangeGradientPreview = useRangeGradientPreview(element.id);
  const hasSvgFill = useMemo(
    () =>
      Boolean(rangeGradientPreview) ||
      shouldUseSvgTextFill({
        element: {
          ...liveTextElement,
          backgroundTextMode: supportedBackgroundTextMode,
        },
        hasGradientFill,
        includeMixedTypography: true,
      }),
    [
      hasGradientFill,
      liveTextElement,
      supportedBackgroundTextMode,
      rangeGradientPreview,
    ]
  );
  const hasGlyphTextShadow =
    getNormalizedTextShadow(element)?.target === TEXT_SHADOW_TARGET.TEXT;

  const { updateElementById, attachmentTransformId, pageElements } = useStory(
    ({ state, actions }) => ({
      updateElementById: actions.updateElementById,
      pageElements: state.currentPage?.elements || [],
      attachmentTransformId: getTextBoxAttachmentTargetTransformId(
        state.currentPage?.elements || [],
        id
      ),
    })
  );

  const { isAnythingTransforming, pushTransform } = useTransform((state) => ({
    isAnythingTransforming: state.state.isAnythingTransforming,
    pushTransform: state.actions.pushTransform,
  }));

  const setProperties = useCallback(
    (properties) => updateElementById({ elementId: id, properties }),
    [id, updateElementById]
  );

  const pageElementsRef = useRef(pageElements);
  pageElementsRef.current = pageElements;
  const liveStackTransformIdsRef = useRef([]);
  const hasLinkedGrowth =
    ['top', 'center', 'bottom'].includes(element.verticalAnchor) &&
    getTextBoxAttachmentStack(pageElements, id).length > 1;
  const hasLinkedGrowthRef = useRef(hasLinkedGrowth);
  hasLinkedGrowthRef.current = hasLinkedGrowth;

  const wrapperRef = useRef(null);
  const highlightRef = useRef(null);
  const highlightBgRef = useRef(null);
  const textBoxRef = useRef(null);
  const editorRef = useRef(null);
  const boxRef = useRef();
  const committedBoxRef = useRef({ x, y, height });
  const editValueRef = useRef({ content, fontReferences });
  const editorHeightRef = useRef(0);
  const outsideBorderRef = useRef(null);

  // x, y, height, rotationAngle changes should not update the content while in edit mode.
  // updateContent should be only called on unmount.
  // Refresh these refs before the layout effect measures the live edit box.
  useLayoutEffect(() => {
    // Keep persisted geometry separate from the rebased live box, without
    // changing the identity of the callback consumed by useUnmount.
    const previousCommittedBox = committedBoxRef.current;
    committedBoxRef.current = { x, y, height };
    if (
      boxRef.current &&
      boxRef.current.verticalAnchor !== element.verticalAnchor &&
      !hasLinkedGrowthRef.current
    ) {
      const liveHeight = editorHeightRef.current || boxRef.current.height;
      boxRef.current = {
        ...getTextElementHeightResizeUpdates(boxRef.current, liveHeight),
        rotationAngle,
        verticalAnchor: element.verticalAnchor,
        textBoxAttachment: element.textBoxAttachment,
      };
      return;
    }

    // An anchor switch can rebase the live box before content is committed.
    // Only replace geometry that actually changed in the persisted element;
    // moving X or rotating must not restore its pre-switch Y/height.
    const previousBox = boxRef.current;
    boxRef.current = {
      x: previousBox && x === previousCommittedBox.x ? previousBox.x : x,
      y: previousBox && y === previousCommittedBox.y ? previousBox.y : y,
      height:
        previousBox && height === previousCommittedBox.height
          ? previousBox.height
          : height,
      rotationAngle,
      verticalAnchor: element.verticalAnchor,
      textBoxAttachment: element.textBoxAttachment,
    };
  }, [
    x,
    y,
    height,
    rotationAngle,
    element.verticalAnchor,
    element.textBoxAttachment,
  ]);

  // Make sure to allow the user to click in the text box while working on the text.
  const onClick = (evt) => {
    const editor = editorRef.current;
    editor.focus();
    evt.stopPropagation();
  };

  // Set focus when initially rendered.
  useLayoutEffect(() => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  const updateContent = useCallback(() => {
    const newHeight = editorHeightRef.current;
    if (wrapperRef.current) {
      wrapperRef.current.style.height = '';
    }
    if (editValueRef.current) {
      const properties = { ...editValueRef.current };
      // Recalculate the new height and offset.
      if (newHeight && boxRef.current) {
        const reflowedElements = getTextBoxAttachmentReflow(
          pageElementsRef.current,
          {
            ...pageElementsRef.current.find((member) => member.id === id),
            id,
            ...boxRef.current,
          },
          newHeight
        );
        const geometry =
          reflowedElements?.find((member) => member.id === id) ||
          getTextElementHeightResizeUpdates(boxRef.current, newHeight);
        const committedBox = committedBoxRef.current;
        // Rounding a half-pixel offset independently of height moves the centre
        // on every edit. Retain fractional geometry for individual or shared centre growth.
        const withRounding =
          !reflowedElements &&
          (boxRef.current.verticalAnchor !== 'center' ||
            isTextBoxAttachment(boxRef.current.textBoxAttachment));
        if (
          Math.abs(geometry.height - committedBox.height) >= 0.01 ||
          geometry.x !== committedBox.x ||
          geometry.y !== committedBox.y
        ) {
          properties.height = editorToDataY(geometry.height, withRounding);
          properties.x = editorToDataX(geometry.x, withRounding);
          properties.y = editorToDataY(geometry.y, withRounding);
        }
      }
      // Clear while the edit refs are still mounted. The Story update can
      // synchronously unmount this editor before transform handlers are removed.
      liveStackTransformIdsRef.current.forEach((memberId) =>
        pushTransform?.(memberId, null)
      );
      liveStackTransformIdsRef.current = [];
      if (attachmentTransformId) {
        pushTransform?.(attachmentTransformId, null);
      }
      setProperties(properties);
    }
  }, [
    id,
    attachmentTransformId,
    editorToDataX,
    editorToDataY,
    pushTransform,
    setProperties,
  ]);

  // Update content for element on unmount.
  useUnmount(updateContent);

  useEffect(() => {
    // If there are any moveable actions happening, let's update the content
    // Otherwise the font size and measures will not be correct.
    if (isAnythingTransforming) {
      updateContent();
    }
  }, [isAnythingTransforming, updateContent]);

  // A function to remeasure height
  const handleResize = useCallback(() => {
    const wrapper = wrapperRef.current;
    const textBox = textBoxRef.current;
    const standardHighlightHeight = getStandardHighlightHeight({
      backgroundTextMode: supportedBackgroundTextMode,
      highlightElement: highlightBgRef.current,
      verticalTrim: element.verticalTrim,
    });
    editorHeightRef.current = verticalTrimHeight
      ? dataToEditorY(verticalTrimHeight)
      : standardHighlightHeight ??
        (standardTrimHeight !== undefined
          ? dataToEditorY(standardTrimHeight)
          : textBox.offsetHeight -
            dataToEditorY(marginOffsetTop) -
            dataToEditorY(marginOffsetBottom));
    wrapper.style.height = `${editorHeightRef.current}px`;
    wrapper.style.setProperty(
      '--text-gradient-height',
      `${editorHeightRef.current}px`
    );
    const { wrapperMarginTop } = getSvgTextVerticalPosition({
      backgroundTextMode: supportedBackgroundTextMode,
      marginOffsetTop: dataToEditorY(marginOffsetTop),
      verticalPadding: textProps.textBoxVerticalPadding,
      verticalTrim: element.verticalTrim,
    });
    textBox.style.margin = hasSvgFill ? `${wrapperMarginTop}px 0` : '';

    if (editWrapper && boxRef.current) {
      const reflowedElements = hasLinkedGrowth
        ? getTextBoxAttachmentReflow(
            pageElementsRef.current,
            { ...element, ...boxRef.current, width: elementWidth },
            editorHeightRef.current
          )
        : null;
      const geometry =
        reflowedElements?.find((member) => member.id === id) ||
        getTextElementHeightResizeUpdates(
          boxRef.current,
          editorHeightRef.current
        );
      const dx = geometry.x - boxRef.current.x;
      const dy = geometry.y - boxRef.current.y;
      editWrapper.style.height = `${outerDataToEditorY(
        editorHeightRef.current
      )}px`;
      editWrapper.style.left = `${outerDataToEditorX(geometry.x)}px`;
      editWrapper.style.top = `${outerDataToEditorY(geometry.y)}px`;

      if (reflowedElements) {
        liveStackTransformIdsRef.current = reflowedElements
          .filter((member) => member.id !== id)
          .map((member) => member.id);
        reflowedElements.forEach((member) => {
          if (member.id === id) return;
          const previous = pageElementsRef.current.find(
            (saved) => saved.id === member.id
          );
          pushTransform?.(member.id, {
            staticTransformation: true,
            attachmentResolved: true,
            translate: [
              outerDataToEditorX(member.x - previous.x),
              outerDataToEditorY(member.y - previous.y),
            ],
            rotate: member.rotationAngle,
            resize: [
              outerDataToEditorX(member.width),
              outerDataToEditorY(member.height),
            ],
          });
        });
      } else {
        liveStackTransformIdsRef.current.forEach((memberId) =>
          pushTransform?.(memberId, null)
        );
        liveStackTransformIdsRef.current = [];
      }
      if (attachmentTransformId && !reflowedElements) {
        pushTransform?.(attachmentTransformId, {
          staticTransformation: true,
          translate: [outerDataToEditorX(dx), outerDataToEditorY(dy)],
          rotate: boxRef.current.rotationAngle,
          resize: [
            outerDataToEditorX(elementWidth),
            outerDataToEditorY(editorHeightRef.current),
          ],
        });
      }

      if (editorHeightRef.current > 0) {
        setSvgBorderProps((borderProps) => ({
          ...borderProps,
          height: editorToDataY(editorHeightRef.current),
        }));
      }
      onResize && onResize();
    }
  }, [
    id,
    element,
    hasLinkedGrowth,
    dataToEditorY,
    outerDataToEditorX,
    outerDataToEditorY,
    editorToDataY,
    editWrapper,
    marginOffsetTop,
    marginOffsetBottom,
    standardTrimHeight,
    hasSvgFill,
    supportedBackgroundTextMode,
    textProps.textBoxVerticalPadding,
    verticalTrimHeight,
    onResize,
    elementWidth,
    attachmentTransformId,
    pushTransform,
  ]);
  // Invoke on each content update.
  const handleUpdate = useCallback(
    (newValue) => {
      const sanitizedValue = {
        ...newValue,
        content: sanitizeRichText(newValue.content),
      };
      editValueRef.current = sanitizedValue;
      setSvgBorderProps((borderProps) => ({
        ...borderProps,
        ...sanitizedValue,
      }));
      handleResize();
    },
    [handleResize]
  );
  // Highlight markup is rendered from editorContent after onChange. Measure
  // again after that render, even when the calculated font metrics stay equal.
  useLayoutEffect(handleResize, [editorContent, elementHeight, handleResize]);

  useEffect(() => {
    maybeEnqueueFontStyle(fontFaceSetConfigs);
  }, [fontFaceSetConfigs, maybeEnqueueFontStyle]);

  const svgMarginOffsetTop = dataToEditorY(marginOffsetTop);
  const { top: svgTop, wrapperMarginTop: svgWrapperMarginTop } =
    getSvgTextVerticalPosition({
      backgroundTextMode: supportedBackgroundTextMode,
      marginOffsetTop: svgMarginOffsetTop,
      verticalPadding: textProps.textBoxVerticalPadding,
      verticalTrim: element.verticalTrim,
    });

  useTransformHandler(id, (transform) => {
    const target = textBoxRef.current;
    const wrapper = wrapperRef.current;
    const highlight = highlightRef.current;
    if (!target || !wrapper) {
      return;
    }
    const updatedFontSize = transform?.updates?.fontSize;
    const typographyScale =
      updatedFontSize && fontSize ? updatedFontSize / fontSize : 1;
    target.style.fontSize = updatedFontSize
      ? `${dataToEditorY(updatedFontSize)}px`
      : '';
    setLiveTypographyScale(target, typographyScale);
    updateInlineFontSizePreview(highlight, typographyScale, dataToEditorY);
    const isWidthOnlyResize =
      transform?.direction?.[0] !== 0 && transform?.direction?.[1] === 0;
    const shouldRerenderPreview =
      hasSvgFill || (hasHighlightBackgroundTextMode && isWidthOnlyResize);
    if (transform && shouldRerenderPreview) {
      setRequestedTypographyScale(typographyScale);
    }
    const updatedMargin = transform
      ? transform.updates?.marginOffsetTop ?? marginOffsetTop * typographyScale
      : marginOffsetTop;
    if (hasSvgFill) {
      const { wrapperMarginTop } = getSvgTextVerticalPosition({
        backgroundTextMode: supportedBackgroundTextMode,
        marginOffsetTop: dataToEditorY(updatedMargin),
        verticalPadding: textProps.textBoxVerticalPadding,
        verticalTrim: element.verticalTrim,
      });
      target.style.margin = `${wrapperMarginTop}px 0`;
    } else {
      target.style.margin =
        transform && updatedMargin
          ? `${dataToEditorY(-updatedMargin)}px 0`
          : '';
    }
    if (highlight) {
      highlight.style.fontSize = target.style.fontSize;
      highlight.style.margin =
        transform && updatedMargin
          ? `${dataToEditorY(-updatedMargin)}px 0`
          : '';
    }

    if (transform === null) {
      setRequestedTypographyScale(1);
      setLiveSvgSize(null);
      wrapper.style.width = '';
      wrapper.style.height = '';
      wrapper.style.setProperty(
        '--text-gradient-width',
        `${dataToEditorX(elementWidth)}px`
      );
      wrapper.style.setProperty(
        '--text-gradient-height',
        `${boxRef.current.height}px`
      );
    } else {
      const { resize } = transform;
      if (resize && resize[0] !== 0 && resize[1] !== 0) {
        const width = outerToNativeX(resize[0]);
        const height = outerToNativeY(resize[1]);
        if (shouldRerenderPreview) {
          setLiveSvgSize({ width, height });
        }
        wrapper.style.width = `${width}px`;
        wrapper.style.height = `${height}px`;
        wrapper.style.setProperty('--text-gradient-width', `${width}px`);
        wrapper.style.setProperty('--text-gradient-height', `${height}px`);
      }
    }
  });

  // For instant color change on selection.
  useCSSVarColorTransformHandler({
    id,
    targetRef: wrapperRef,
    cssVar: '--faux-selection-color',
    expectedStyle: 'color',
  });
  useCSSVarColorTransformHandler({
    id,
    targetRef: wrapperRef,
    cssVar: '--st-caret-preview-color',
    expectedStyle: 'color',
  });

  const backgroundRef = hasHighlightBackgroundTextMode
    ? highlightBgRef
    : wrapperRef;
  useColorTransformHandler({
    id,
    targetRef: hasTextBoxBackdrop ? null : backgroundRef,
    expectedStyle: 'background',
  });

  // Inner and center color are handled separately, add transform for outside border if relevant.
  useColorTransformHandler({
    id,
    targetRef: shouldDisplayBorder(element) ? outsideBorderRef : null,
    expectedStyle: 'border-color',
  });

  const fontSizeToCss = useCallback(
    (value) => getLiveTypographyFontSizeCss(value, dataToEditorY),
    [dataToEditorY]
  );
  const wrapperBackgroundColor =
    supportedBackgroundTextMode === BACKGROUND_TEXT_MODE.FILL &&
    !hasTextBoxBackdrop &&
    backgroundColor;

  const wrapperProps = {
    backgroundColor: wrapperBackgroundColor,
  };

  const shouldSyncSvgEditOffset = shouldSyncSvgTextWithHtml({
    element: liveTextElement,
    hasSvgFill,
    isHighlight: hasHighlightBackgroundTextMode,
  });
  const gradientFill = hasGradientFill && !hasSvgFill ? element.color : null;
  const fallbackCaretColor = useMemo(() => {
    if (element.textTexture?.src && element.textTexture.fallbackColor) {
      return (
        generatePatternStyles(element.textTexture.fallbackColor, 'color')
          .color || DEFAULT_GRADIENT_FALLBACK_COLOR
      );
    }

    if (hasGradient(element.color)) {
      return (
        getGradientFallbackColor(element.color) ||
        DEFAULT_GRADIENT_FALLBACK_COLOR
      );
    }

    const solidColor =
      element.color &&
      generatePatternStyles(element.color, 'color', element.color.id).color;

    return solidColor || DEFAULT_GRADIENT_FALLBACK_COLOR;
  }, [element.color, element.textTexture]);
  const fauxSelectionColor = hasSvgFill ? fallbackCaretColor : null;
  const textGradientStyleVars =
    gradientFill && !hasSvgFill
      ? {
          '--text-gradient-width': `${dataToEditorX(elementWidth)}px`,
          '--text-gradient-height': `${height}px`,
          '--text-gradient-offset-x': `${-dataToEditorX(
            rest.padding?.horizontal || 0
          )}px`,
          '--text-gradient-offset-y': `${-dataToEditorY(
            rest.padding?.vertical || 0
          )}px`,
        }
      : null;
  const wrapperStyle =
    fauxSelectionColor || textGradientStyleVars
      ? {
          ...(fauxSelectionColor
            ? { '--faux-selection-color': fauxSelectionColor }
            : null),
          ...(textGradientStyleVars || null),
        }
      : undefined;
  const highlightContent = useMemo(
    () =>
      hasHighlightBackgroundTextMode && editorContent
        ? getMeasuredLineBreakContent({
            element: {
              ...element,
              content: previewContent,
              fontReferences: svgBorderProps.fontReferences,
              fontSize: element.fontSize * liveTypographyScale,
            },
            dataToEditorX,
            dataToEditorY,
            fontSizeAttributeScale: liveTypographyScale,
            materializeLineHeights: hasMixedLineHeight,
            lineHeightCss: usesMixedTypographyHighlightLayout
              ? getHighlightLineheight(
                  element.lineHeight,
                  dataToEditorY(element.padding?.vertical || 0)
                )
              : undefined,
            lineBandHeightCss: usesMixedTypographyHighlightLayout
              ? getHighlightLineheight(
                  1,
                  dataToEditorY(element.padding?.vertical || 0)
                )
              : undefined,
            verticalPadding: dataToEditorY(element.padding?.vertical || 0),
            wrapLines: usesMixedTypographyHighlightLayout,
            width: liveSvgSize?.width ?? dataToEditorX(elementWidth),
          }) ||
          materializeTextContent(
            {
              ...liveTextElement,
              fontSize: element.fontSize * liveTypographyScale,
            },
            {
              fontSizeToCss: (value) =>
                fontSizeToCss(value * liveTypographyScale),
            }
          )
        : editorContent,
    [
      dataToEditorX,
      dataToEditorY,
      editorContent,
      element,
      elementWidth,
      fontSizeToCss,
      fontLoaded,
      hasHighlightBackgroundTextMode,
      hasMixedLineHeight,
      liveTextElement,
      liveSvgSize,
      liveTypographyScale,
      previewContent,
      svgBorderProps.fontReferences,
      usesMixedTypographyHighlightLayout,
    ]
  );
  useEffect(() => {
    if (!shouldSyncSvgEditOffset) {
      setSvgEditOffset((currentOffset) =>
        currentOffset.x === 0 && currentOffset.y === 0
          ? currentOffset
          : { x: 0, y: 0 }
      );
      return undefined;
    }

    const syncSvgEditOffset = () => {
      const wrapper = wrapperRef.current;
      const textRectRoot = hasHighlightBackgroundTextMode
        ? highlightBgRef.current
        : wrapper?.querySelector('.DraftEditor-editorContainer');
      const editorRect = getFirstTextRect(textRectRoot);
      const svgRect = getFirstSvgTextRect(wrapper?.querySelector('svg'));

      if (!editorRect || !svgRect) {
        return;
      }

      setSvgEditOffset((currentOffset) => {
        const nextOffset = {
          x: currentOffset.x + outerToNativeX(editorRect.x - svgRect.x),
          y: currentOffset.y + outerToNativeY(editorRect.y - svgRect.y),
        };

        if (
          Math.abs(currentOffset.x - nextOffset.x) < 0.5 &&
          Math.abs(currentOffset.y - nextOffset.y) < 0.5
        ) {
          return currentOffset;
        }

        return nextOffset;
      });
    };

    syncSvgEditOffset();

    const animationFrameId = requestAnimationFrame(syncSvgEditOffset);

    return () => cancelAnimationFrame(animationFrameId);
  }, [
    fontLoaded,
    shouldSyncSvgEditOffset,
    hasHighlightBackgroundTextMode,
    element.verticalTrim,
    liveSvgSize,
    liveTypographyScale,
    outerToNativeX,
    outerToNativeY,
    previewContent,
    svgMarginOffsetTop,
    svgTop,
    textProps.fontSize,
    rest.lineHeight,
  ]);

  return (
    <OutsideBorder
      ref={outsideBorderRef}
      $allowTextOverflow={hasSvgFill && element.verticalTrim === 'baseline'}
      border={border}
      borderRadius={borderRadius}
      width={elementWidth}
      height={elementHeight}
    >
      {/* onClick handler is needed here to ensure the editor keeps focus, e.g. after setting inline colour. */}
      <Wrapper
        ref={wrapperRef}
        onClick={onClick}
        data-testid="textEditor"
        style={wrapperStyle}
        $applyTextBoxShadow={shouldApplyFillTextBoxShadow({
          element,
          backgroundTextMode: supportedBackgroundTextMode,
          hasTextBoxBackdrop,
        })}
        element={element}
        dataToEditorX={dataToEditorX}
        dataToEditorY={dataToEditorY}
        {...wrapperProps}
      >
        {editorContent && hasHighlightBackgroundTextMode && (
          <TextBoxPadded ref={highlightRef} {...highlightTextProps}>
            <Highlight
              ref={highlightBgRef}
              $hasMixedLineHeight={hasMixedLineHeight}
              $usesMixedTypographyLayout={usesMixedTypographyHighlightLayout}
              borderRadius={borderRadius}
              dataToEditorY={dataToEditorY}
              dangerouslySetInnerHTML={{ __html: highlightContent }}
              {...textProps}
            />
          </TextBoxPadded>
        )}
        <EditTextBox
          hasHighlightBackgroundTextMode={hasHighlightBackgroundTextMode}
          className="syncMargin"
          ref={textBoxRef}
          hasSvgFill={hasSvgFill}
          style={
            hasSvgFill ? { margin: `${svgWrapperMarginTop}px 0` } : undefined
          }
          {...textProps}
          $matchSvgListMetrics={hasSvgFill && hasMixedTypography}
        >
          <RichTextEditorWrapper
            $gradientFill={gradientFill}
            $materializedLineHeight={textProps.lineHeight}
            isVisible={!hasSvgFill}
          >
            <RichTextEditor
              ref={editorRef}
              sessionKey={id}
              content={content}
              fontReferences={fontReferences}
              defaultFont={font}
              defaultFontSize={fontSize}
              defaultLineHeight={element.lineHeight}
              defaultFontWeight={element.fontWeight}
              defaultFontStyle={element.fontStyle}
              fontSizeToCss={fontSizeToCss}
              lineHeightToCss={editorLineHeightToCss}
              materializeFontSizes={materializeEditorFontSizes}
              materializeLineHeights={hasMixedLineHeight}
              fallbackCaretColor={fallbackCaretColor}
              preferFallbackCaretColor={Boolean(element.textTexture?.src)}
              screenSpaceScale={
                !rotationAngle &&
                element.textAlign !== 'justify' &&
                !hasSvgFill &&
                !hasGradientFill &&
                !hasHighlightBackgroundTextMode &&
                !hasList &&
                !hasGlyphTextShadow &&
                !materializeEditorFontSizes &&
                !hasMixedLineHeight
                  ? outerDataToEditorY(1)
                  : undefined
              }
              onChange={handleUpdate}
            />
          </RichTextEditorWrapper>
          {hasSvgFill && (
            <SvgTextDisplay
              cachePreview
              initialGradientPreview={rangeGradientPreview}
              element={{
                ...element,
                content: previewContent,
                fontReferences: svgBorderProps.fontReferences,
                fontSize: element.fontSize * liveTypographyScale,
              }}
              font={font}
              fontLoaded={fontLoaded}
              paddingBetweenLines={
                hasHighlightBackgroundTextMode ? textProps.verticalPadding : 0
              }
              textureOrigin={{
                x:
                  -dataToEditorX(rest.padding?.horizontal || 0) -
                  svgEditOffset.x,
                y: -svgTop - svgWrapperMarginTop - svgEditOffset.y,
              }}
              top={svgTop}
              left={dataToEditorX(rest.padding?.horizontal || 0)}
              dataToEditorX={dataToEditorX}
              dataToEditorY={dataToEditorY}
              fontSize={textProps.fontSize * liveTypographyScale}
              height={
                liveSvgSize?.height ??
                (heightWithoutVerticalTrim || svgBorderProps.height
                  ? dataToEditorY(
                      heightWithoutVerticalTrim || svgBorderProps.height
                    )
                  : undefined)
              }
              lineHeight={rest.lineHeight}
              width={liveSvgSize?.width}
              styles={{
                transform:
                  svgEditOffset.x || svgEditOffset.y
                    ? `translate(${svgEditOffset.x}px, ${svgEditOffset.y}px)`
                    : undefined,
              }}
              type="edit"
            />
          )}
        </EditTextBox>
      </Wrapper>
    </OutsideBorder>
  );
}

TextEditCanvas.propTypes = {
  element: StoryPropTypes.elements.text.isRequired,
  box: StoryPropTypes.box.isRequired,
  onResize: PropTypes.func,
  editWrapper: PropTypes.object,
  hasTextBoxBackdrop: PropTypes.bool,
  outerDataToEditorX: PropTypes.func.isRequired,
  outerDataToEditorY: PropTypes.func.isRequired,
  outerToNativeX: PropTypes.func.isRequired,
  outerToNativeY: PropTypes.func.isRequired,
};

function TextEdit(props) {
  const { element } = props;
  const nativeBox = useMemo(
    () => ({
      x: element.x,
      y: element.y,
      width: element.width,
      height: element.height,
      rotationAngle: element.rotationAngle,
    }),
    [element.height, element.rotationAngle, element.width, element.x, element.y]
  );

  return (
    <NativeTextLayout>
      {({ dataToEditorX, dataToEditorY, toNativeX, toNativeY }) => (
        <TextEditCanvas
          {...props}
          box={nativeBox}
          outerDataToEditorX={dataToEditorX}
          outerDataToEditorY={dataToEditorY}
          outerToNativeX={toNativeX}
          outerToNativeY={toNativeY}
        />
      )}
    </NativeTextLayout>
  );
}

TextEdit.propTypes = {
  element: StoryPropTypes.elements.text.isRequired,
  box: StoryPropTypes.box.isRequired,
  onResize: PropTypes.func,
  editWrapper: PropTypes.object,
  hasTextBoxBackdrop: PropTypes.bool,
};

export default TextEdit;
